import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { fetchCdnJson } from "@/api/fetchCdnJson";
import type { Game, SportGame } from "./gameCatalog";

const toCssVariable = (key: string): string => {
  const baseKey = key.endsWith("Color") ? key.slice(0, -5) : key;
  return `--color-${baseKey.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
};

const generateColorVariants = (color: string) => {
  return {
    "100": color,
    "80": adjustOpacity(color, 0.8),
    "60": adjustOpacity(color, 0.6),
    "40": adjustOpacity(color, 0.4),
    "20": adjustOpacity(color, 0.2),
    "10": adjustOpacity(color, 0.1),
    "5": adjustOpacity(color, 0.05),
  };
};

const adjustOpacity = (color: string, opacity: number): string => {
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const a = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0");

  const rgbHex = [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");

  return `#${rgbHex}${a}`;
};

export const setPromoColors = (colors: Record<string, string>) => {
  const root = document.documentElement;

  Object.entries(colors).forEach(([key, value]) => {
    if (typeof value !== "string" || !value?.startsWith("#")) {
      return;
    }

    const cssVarBase = toCssVariable(key);
    const variants = generateColorVariants(value);

    Object.entries(variants).forEach(([opacity, color]) => {
      const varName = opacity === "100" ? cssVarBase : `${cssVarBase}-${opacity}`;
      root.style.setProperty(varName, color);
    });

    if (key.toLowerCase().includes("toast")) {
      const toastVar = `--color-${key.toLowerCase()}`;
      root.style.setProperty(toastVar, value);
    }
  });
};

export const useQueryWithNoCache = (
  queryKey: unknown[],
  url: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    select?: (data: any) => any;
    [key: string]: any;
  }
) => {
  return useQuery({
    queryKey,
    queryFn: () =>
      axios.get(url, {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }),
    ...options,
  });
};

const deserializeGame = (res: any): Game => {
  const { i, eid, n, l, p, pn, dn, epid, img, lk, c, ...rest } = res ?? {};

  return {
    ...rest,
    id: String(i ?? rest.id ?? ""),
    extGameId: String(eid ?? rest.extGameId ?? ""),
    name: n || rest.name,
    providerName: p || rest.providerName,
    extProviderId: epid || rest.extProviderId || "",
    providerDisplayName: pn || rest.providerDisplayName || "",
    isLive: l ?? rest.isLive,
    displayName: dn || rest.displayName,
    image: img || rest.image || null,
    link: lk || rest.link || null,
    category: c || rest.category,
  };
};

const deserializeSportGame = (res: any): SportGame => {
  const { i, st, dn, t, tdn, img, lk, l, ...rest } = res ?? {};
  const sportType = st || rest.sportType || "";
  const tournament = t || rest.tournament || "";

  return {
    ...rest,
    id: String(i || rest.id || `${sportType}:${tournament}`),
    sportType,
    sportDisplayName: dn || rest.displayName || rest.sportDisplayName || sportType,
    tournament,
    tournamentDisplayName: tdn || rest.tournamentDisplayName || tournament,
    image: img || rest.image || null,
    link: lk || rest.link || null,
    isLive: l ?? rest.isLive,
  };
};

export const deserializeGames = (data: any[] = []): Game[] => data.map(deserializeGame);

export const deserializeSportGames = (data: any[] = []): SportGame[] =>
  data.map(deserializeSportGame);

const CATALOG_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  gcTime: Infinity,
  refetchOnMount: false,
} as const;

export const useGamesQuery = (orgUuid: string | undefined, enabled = true) => {
  const url = `${import.meta.env.VITE_WIDGET_CDN_BASE_URL}/organization/${orgUuid}/casino/games/games.json`;

  return useQuery({
    queryKey: ["games", orgUuid],
    queryFn: () => fetchCdnJson(url).then(deserializeGames),
    enabled: enabled && !!orgUuid,
    ...CATALOG_QUERY_OPTIONS,
  });
};

export const useSportGamesQuery = (orgUuid: string | undefined, enabled = true) => {
  const url = `${import.meta.env.VITE_WIDGET_CDN_BASE_URL}/organization/${orgUuid}/casino/games/sport_games.json`;

  return useQuery({
    queryKey: ["sportGames", orgUuid],
    queryFn: () => fetchCdnJson(url).then(deserializeSportGames),
    enabled: enabled && !!orgUuid,
    ...CATALOG_QUERY_OPTIONS,
  });
};
